import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellRing, X, Plus, TrendingUp, TrendingDown, DollarSign, Percent } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_TYPES = [
  { key: 'price_above', label: 'Price Above', icon: TrendingUp, unit: '$' },
  { key: 'price_below', label: 'Price Below', icon: TrendingDown, unit: '$' },
  { key: 'yield_above', label: 'Yield Above', icon: Percent, unit: '%' },
  { key: 'yield_below', label: 'Yield Below', icon: Percent, unit: '%' },
];

function AlertRow({ alert, onDelete }) {
  const typeInfo = ALERT_TYPES.find(t => t.key === alert.alert_type) || ALERT_TYPES[0];
  const Icon = typeInfo.icon;

  return (
    <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${
      alert.triggered ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700' : 'bg-card border-border'
    }`} data-testid={`alert-row-${alert.alert_id}`}>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
          alert.triggered ? 'bg-amber-500/20 text-amber-600' : 'bg-muted text-muted-foreground'
        }`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-bold text-yellow-600 dark:text-yellow-400">{alert.ticker}</span>
            <span className="text-[10px] text-muted-foreground">{typeInfo.label}</span>
            <span className="text-xs font-mono font-bold">{typeInfo.unit === '$' ? '$' : ''}{alert.threshold}{typeInfo.unit === '%' ? '%' : ''}</span>
          </div>
          {alert.triggered && (
            <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-0.5">
              <BellRing className="w-3 h-3" /> Triggered!
              {alert.current_price > 0 && ` (Now: $${alert.current_price.toFixed(2)})`}
            </span>
          )}
          {alert.note && <p className="text-[10px] text-muted-foreground truncate">{alert.note}</p>}
        </div>
      </div>
      <button
        onClick={() => onDelete(alert.alert_id)}
        className="p-1 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded transition-colors shrink-0"
        data-testid={`alert-delete-${alert.alert_id}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function AlertsBadge({ count, triggered, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-lg hover:bg-muted transition-colors"
      data-testid="alerts-badge-btn"
      title="Price & Dividend Alerts"
    >
      {triggered > 0 ? (
        <BellRing className="w-4 h-4 text-amber-500 animate-pulse" />
      ) : (
        <Bell className="w-4 h-4 text-muted-foreground" />
      )}
      {count > 0 && (
        <span className={`absolute -top-0.5 -right-0.5 text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center ${
          triggered > 0 ? 'bg-amber-500 text-white' : 'bg-muted-foreground/30 text-foreground'
        }`}>{count}</span>
      )}
    </button>
  );
}

export default function AlertsPanel({ open, onClose }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTicker, setNewTicker] = useState('');
  const [newType, setNewType] = useState('price_above');
  const [newThreshold, setNewThreshold] = useState('');
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchAlerts = () => {
    setLoading(true);
    axios.get(`${API}/alerts`, { withCredentials: true })
      .then(res => setAlerts(res.data || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) fetchAlerts();
  }, [open]);

  const handleDelete = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`, { withCredentials: true });
      setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
    } catch {}
  };

  const handleAdd = async () => {
    if (!newTicker.trim() || !newThreshold) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/alerts`, {
        ticker: newTicker.trim().toUpperCase(),
        alert_type: newType,
        threshold: parseFloat(newThreshold),
        note: newNote,
      }, { withCredentials: true });
      setAlerts(prev => [...prev, data]);
      setNewTicker('');
      setNewThreshold('');
      setNewNote('');
      setShowAdd(false);
    } catch {}
    setSaving(false);
  };

  const triggeredCount = alerts.filter(a => a.triggered).length;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end pt-16 pr-4" data-testid="alerts-panel-overlay">
      <div className="fixed inset-0 bg-black/20" onClick={onClose} />
      <div className="relative bg-card border rounded-xl shadow-2xl w-full max-w-sm max-h-[70vh] overflow-hidden flex flex-col z-10" data-testid="alerts-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-bold">Price & Dividend Alerts</h3>
            {triggeredCount > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">{triggeredCount} triggered</span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors" data-testid="alerts-panel-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Alerts List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2 pb-24 sm:pb-4">
          {loading ? (
            <div className="flex items-center justify-center h-20">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No alerts set</p>
              <p className="text-xs mt-1">Create alerts to track price and yield targets</p>
            </div>
          ) : (
            alerts.map(alert => (
              <AlertRow key={alert.alert_id} alert={alert} onDelete={handleDelete} />
            ))
          )}
        </div>

        {/* Add Alert */}
        <div className="border-t p-3">
          {showAdd ? (
            <div className="space-y-2" data-testid="alert-add-form">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Ticker (e.g. AAPL)"
                  value={newTicker}
                  onChange={e => setNewTicker(e.target.value)}
                  className="flex-1 h-8 rounded-md border bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="alert-ticker-input"
                />
                <select
                  value={newType}
                  onChange={e => setNewType(e.target.value)}
                  className="h-8 rounded-md border bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="alert-type-select"
                >
                  {ALERT_TYPES.map(t => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Threshold"
                  value={newThreshold}
                  onChange={e => setNewThreshold(e.target.value)}
                  className="flex-1 h-8 rounded-md border bg-background px-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="alert-threshold-input"
                />
                <input
                  type="text"
                  placeholder="Note (optional)"
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  className="flex-1 h-8 rounded-md border bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="alert-note-input"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAdd}
                  disabled={saving || !newTicker.trim() || !newThreshold}
                  className="flex-1 h-8 rounded-md bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-50 transition-colors"
                  data-testid="alert-save-btn"
                >
                  {saving ? 'Saving...' : 'Add Alert'}
                </button>
                <button
                  onClick={() => setShowAdd(false)}
                  className="h-8 px-3 rounded-md border text-xs font-medium hover:bg-muted transition-colors"
                  data-testid="alert-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAdd(true)}
              className="w-full h-8 rounded-md border border-dashed text-xs font-medium text-muted-foreground hover:text-foreground hover:border-foreground/30 flex items-center justify-center gap-1.5 transition-colors"
              data-testid="alert-add-btn"
            >
              <Plus className="w-3.5 h-3.5" /> New Alert
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
